import { CDN_BASE_PATH } from './itemData';
import type { SelectedItems } from './types';

const CANVAS_WIDTH = 154;
const CANVAS_HEIGHT = 148;

/**
 * 선택된 아이템들을 캔버스에 합성하여 Blob으로 반환합니다.
 * @param selectedItems 선택된 아이템들
 * @param frameImagePath 프레임 이미지 경로 (옵션)
 * @param outputWidth 출력 이미지 너비 (옵션, 기본값: CANVAS_WIDTH)
 * @param outputHeight 출력 이미지 높이 (옵션, 기본값: 비율 유지)
 */
export const composeCharacterImage = async (
  selectedItems: SelectedItems,
  frameImagePath?: string,
  outputWidth?: number,
  outputHeight?: number,
): Promise<Blob> => {
  // 출력 크기 계산
  const targetWidth = outputWidth || CANVAS_WIDTH;
  const targetHeight = outputHeight || CANVAS_HEIGHT * (targetWidth / CANVAS_WIDTH);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context를 생성할 수 없습니다.');
  }

  // 레이어 순서대로 정렬 (background -> top -> expression -> hat -> effect)
  const layers = [
    selectedItems.background,
    selectedItems.top,
    selectedItems.expression,
    selectedItems.hat,
    selectedItems.effect,
  ].filter(Boolean);

  // 이미지 로드 및 그리기
  for (const item of layers) {
    if (!item) continue;

    // 이미지 경로인 경우 (CDN 경로)
    if (item.emoji.startsWith(CDN_BASE_PATH)) {
      const img = await loadImage(item.emoji);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    } else {
      // 이모지인 경우 (effect 카테고리)
      drawEmoji(ctx, item.emoji, targetWidth, targetHeight);
    }
  }

  // 프레임 이미지가 있는 경우 최상단에 1.4배 크기로 그리기 (중앙 정렬)
  if (frameImagePath) {
    const frameImg = await loadImage(frameImagePath);
    const scaledWidth = targetWidth * 1.4;
    const scaledHeight = targetHeight * 1.4;
    const offsetX = (targetWidth - scaledWidth) / 2;
    const offsetY = (targetHeight - scaledHeight) / 2;
    ctx.drawImage(frameImg, offsetX, offsetY, scaledWidth, scaledHeight);
  }

  // Canvas를 Blob으로 변환
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('이미지 변환에 실패했습니다.'));
        }
      },
      'image/png',
      1.0,
    );
  });
};

/**
 * 이미지 URL을 로드하여 HTMLImageElement로 반환합니다.
 */
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`이미지 로드 실패: ${src}`));
    img.src = src;
  });
};

/**
 * 캔버스에 이모지를 그립니다.
 */
const drawEmoji = (ctx: CanvasRenderingContext2D, emoji: string, width: number, height: number) => {
  ctx.font = '48px serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, width / 2, height / 2);
};

/**
 * Blob을 File 객체로 변환합니다.
 */
export const blobToFile = (blob: Blob, filename: string): File => {
  return new File([blob], filename, { type: blob.type });
};
